// @flow
import React, { Component } from "react";
import classNames from "classnames";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CompareIcon from "@material-ui/icons/Compare";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../../global/types/GeneralTypes";
import type { ButtonStylesClassesType } from "../../../global/styles/buttonStyles";
import type { IconStylesClassesType } from "../../../global/styles/iconStyles";
import type { PanelStylesClassesType } from "../../../global/styles/generalStyles";
import type {
  EyeType,
  FaceDetectionResultType
} from "../../types/FaceEyeTypes";
import {
  panelStyles,
  topBanner,
  greyLabel,
  greenLabel,
  pinkLabel,
  redLabel
} from "../../../global/styles/generalStyles";
import { buttonStyles } from "../../../global/styles/buttonStyles";
import { iconStyles } from "../../../global/styles/iconStyles";
import ErrorDisplay, {
  setErrorMsg
} from "../../../global/components/ErrorDisplay";
import { LoadingProgress } from "imagus-components";
import DisplayFace from "../people/DisplayFace";
import ImageUploadProcess from "./ImageUploadProcess";
import EnrolmentItem from "../people/EnrolmentItem";

const styles = (theme: DictionaryType): DictionaryType => ({
  ...panelStyles(theme),
  ...buttonStyles(theme),
  ...iconStyles(theme),
  topBanner,
  greyLabel,
  greenLabel,
  pinkLabel,
  redLabel,
  clearBoth: {
    clear: "both"
  },
  topMargin: {
    marginTop: "10px"
  },
  rightMargin: {
    marginRight: "5px"
  },
  floatingBtn: {
    padding: "8px"
  }
});

type EnrolItemType = {
  imgToken: string,
  imgUrl: string,
  imgEyes: EyeType
};

type FaceVerifyStatesType = {
  errors: DictionaryType,
  left_enrolments: Array<EnrolItemType>,
  right_enrolments: Array<EnrolItemType>,
  result: number | null,
  loading: boolean
};

type FaceVerifyClassesType = {
  ...PanelStylesClassesType,
  ...ButtonStylesClassesType,
  ...IconStylesClassesType,
  appPanel: DictionaryType,
  appPanelContent: DictionaryType,
  topBanner: DictionaryType,
  greyLabel: DictionaryType,
  greenLabel: DictionaryType,
  pinkLabel: DictionaryType,
  redLabel: DictionaryType,
  clearBoth: DictionaryType,
  topMargin: DictionaryType,
  rightMargin: DictionaryType,
  floatingBtn: DictionaryType
};

type FaceVerifyPropsType = {
  classes: FaceVerifyClassesType,
  faceVerify: Function,
  detectedFaces: Array<FaceDetectionResultType>,
  uploadFile: Function
};

class FaceVerifyLayout extends Component<
  FaceVerifyPropsType,
  FaceVerifyStatesType
> {
  constructor(props: FaceVerifyPropsType) {
    super(props);
    this.state = {
      errors: {},
      left_enrolments: [],
      right_enrolments: [],
      result: null,
      loading: false
    };
  }

  setImageError = (error: string) => {
    this.setState({
      errors: setErrorMsg(this.state.errors, "image", error)
    });
  };

  addLeftEnrolement = (enrolment: EnrolItemType) => {
    let enrolments = this.state.left_enrolments;
    enrolments.push(enrolment);
    this.setState({ left_enrolments: enrolments });
  };

  addRightEnrolement = (enrolment: EnrolItemType) => {
    let enrolments = this.state.right_enrolments;
    enrolments.push(enrolment);
    this.setState({ right_enrolments: enrolments });
  };

  deleteLeftPhoto = (token: string) => {
    let enrolments = this.state.left_enrolments;
    let index = enrolments.findIndex(
      (x: EnrolItemType): * => x.imgToken === token
    );
    enrolments.splice(index, 1);
    this.setState({ left_enrolments: enrolments });
  };

  deleteRightPhoto = (token: string) => {
    let enrolments = this.state.right_enrolments;
    let index = enrolments.findIndex(
      (x: EnrolItemType): * => x.imgToken === token
    );
    enrolments.splice(index, 1);
    this.setState({ right_enrolments: enrolments });
  };

  verify = (): * => {
    this.setState({ loading: true });

    let leftFaces = [];
    const left = this.state.left_enrolments;
    for (let i = 0; i < left.length; i++) {
      leftFaces.push({ token: left[i].imgToken, eyes: left[i].imgEyes });
    }

    let rightFaces = [];
    const right = this.state.right_enrolments;
    for (let i = 0; i < right.length; i++) {
      rightFaces.push({ token: right[i].imgToken, eyes: right[i].imgEyes });
    }

    this.props.faceVerify(leftFaces, rightFaces, this.setResult);
  };

  setResult = (result: number): * => {
    this.setState({ result: result, loading: false });
  };

  render(): React$Element<*> {
    const classes = this.props.classes;

    let leftEnrolments;
    if (this.state.left_enrolments.length > 0) {
      leftEnrolments = this.state.left_enrolments.map(
        (enrol: EnrolItemType, i: number): React$Element<*> => (
          <EnrolmentItem
            key={"left-thumb-" + i}
            imgUrl={enrol.imgUrl}
            deletePhoto={(): Function => this.deleteLeftPhoto(enrol.imgToken)}
          />
        )
      );
    } else {
      leftEnrolments = <DisplayFace key={"left-thumb-none"} isThumbNail />;
    }

    let rightEnrolments;
    if (this.state.right_enrolments.length > 0) {
      rightEnrolments = this.state.right_enrolments.map(
        (enrol: EnrolItemType, i: number): React$Element<*> => (
          <EnrolmentItem
            key={"right-thumb-" + i}
            imgUrl={enrol.imgUrl}
            deletePhoto={(): Function => this.deleteRightPhoto(enrol.imgToken)}
          />
        )
      );
    } else {
      rightEnrolments = <DisplayFace key={"right-thumb-none"} isThumbNail />;
    }

    return (
      <div className={classes.appPanel}>
        <div className={classes.appPanelContent}>
          <div className={classes.topBanner}>
            <ErrorDisplay errors={this.state.errors} />
          </div>
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  {leftEnrolments}
                  <div className={classes.clearBoth} />
                </CardContent>
                <ImageUploadProcess
                  detectedFaces={this.props.detectedFaces}
                  uploadFile={this.props.uploadFile}
                  buttonId="left-upload"
                  setImageError={this.setImageError}
                  addEnrolement={this.addLeftEnrolement}
                  outerClass={classes.floatingBtn}
                  isFab
                  key="left-btn"
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  {rightEnrolments}
                  <div className={classes.clearBoth} />
                </CardContent>
                <ImageUploadProcess
                  detectedFaces={this.props.detectedFaces}
                  uploadFile={this.props.uploadFile}
                  buttonId="right-upload"
                  setImageError={this.setImageError}
                  addEnrolement={this.addRightEnrolement}
                  outerClass={classes.floatingBtn}
                  isFab
                  key="right-btn"
                />
              </Card>
            </Grid>
            <Grid item xs={12} className={classes.topMargin}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                className={classes.darkBtn}
                onClick={this.verify}
                disabled={
                  this.state.left_enrolments.length === 0 ||
                  this.state.right_enrolments.length === 0
                }
              >
                <CompareIcon
                  className={classNames(classes.lightIcon, classes.rightMargin)}
                />
                Verify Photos
              </Button>
            </Grid>
            <Grid item xs={12} className={classes.topMargin}>
              {this.state.loading ? (
                <LoadingProgress size={24} />
              ) : (
                this.state.result !== null && (
                  <Typography variant="h6">
                    Probability:&nbsp;
                    <span
                      className={ProbLabelColor(classes, this.state.result)}
                    >
                      {this.state.result.toFixed(2)}%
                    </span>
                  </Typography>
                )
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }
}

function ProbLabelColor(
  classes: FaceVerifyClassesType,
  prob: number
): DictionaryType {
  if (Number.isNaN(prob)) {
    return classes.greyLabel;
  } else {
    if (prob > 85) {
      return classes.greenLabel;
    } else if (prob <= 85 && prob > 50) {
      return classes.pinkLabel;
    } else {
      return classes.redLabel;
    }
  }
}

export default withStyles(styles)(FaceVerifyLayout);
